import { useColorScheme } from '@myosh/design-system/react';
import { useAuth, useControlledUserSelect, useUser } from '@myosh/myosh-login';
import {
  UserMenu,
  UserMenuItem,
  UserMenuRadioGroup,
  UserMenuRadioItem,
  UserMenuSeparator,
} from '@myosh/odin-components';
import { memo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useGetEnvironmentQuery } from '../../api/generated/v4-api';
import { DIAGRAM_MODE } from '../../helpers/constants';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectDiagramMode, setDiagramMode as toggleDiagram } from '../../redux/slices/diagram';

interface SettingsProps {
  showOnlyAboutOption?: boolean;
}

const Settings = ({ showOnlyAboutOption = false }: SettingsProps) => {
  const dispatch = useAppDispatch();
  const diagramMode = useAppSelector(selectDiagramMode);
  const navigate = useNavigate();
  const location = useLocation();

  const { state } = useUser();
  const { logout } = useAuth();
  const { data: schemaInfo } = useGetEnvironmentQuery();
  const isSuperUser = state.user?.superuser === 'true';
  const { onSelectUser, dialog: controlledUserSelectDialog } = useControlledUserSelect();
  const [colorScheme, setColorScheme] = useColorScheme();

  const setDiagramMode = (selectedDiagramMode: DIAGRAM_MODE) => {
    dispatch(toggleDiagram(selectedDiagramMode));
  };

  const handleLogout = () => {
    logout();
    localStorage.clear();
  };

  const openConfiguration = () => {
    navigate('/configuration', { state: { from: `${location.pathname}${location.search}` } });
  };

  return (
    <>
      <UserMenu
        user={state.user}
        schemaInfo={schemaInfo}
        about={{
          version: process.env.REACT_APP_VERSION,
          info: { openSourceNotices: 'React Flow © xyflow – MIT' },
        }}
        onSelectUser={onSelectUser}
        onLogOut={handleLogout}
        colorScheme={colorScheme}
        onColorSchemeChange={setColorScheme}
      >
        {!showOnlyAboutOption && (
          <>
            <UserMenuRadioGroup value={diagramMode} onValueChange={setDiagramMode}>
              <UserMenuRadioItem
                icon="Network"
                label="Bowtie"
                value={DIAGRAM_MODE.BOWTIE}
                data-cy="settings-item-bowtie"
              />
              <UserMenuRadioItem
                icon="GitFork"
                label="Butterfly"
                value={DIAGRAM_MODE.BUTTERFLY}
                data-cy="settings-item-butterfly"
              />
            </UserMenuRadioGroup>
            {isSuperUser && (
              <>
                <UserMenuSeparator />
                <UserMenuItem
                  icon="Settings"
                  label="Configuration"
                  data-cy="settings-item-configuration"
                  onClick={openConfiguration}
                />
              </>
            )}
          </>
        )}
      </UserMenu>
      {controlledUserSelectDialog}
    </>
  );
};

export default memo(Settings);
